import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import { useScenesStore } from './scenes'
import { useLightsStore } from './lights'
import type { WledEffectSettings } from './lights'

// Light state that goes in the target_state JSONB column
export interface LightState {
  on: boolean
  brightness?: number | null    // Brightness 1-254
  hue?: number | null          // Hue 0-65535
  saturation?: number | null   // Saturation 0-254
  color_temperature?: number | null // Color temperature 153-500
  xy?: [number, number] | null // CIE color coordinates
}

// TypeScript interfaces based on the actual database schema
export interface SceneLightConfig {
  id: string
  scene_id: string
  light_id: string
  target_state: LightState    // JSONB column containing light configuration
  transition_duration: number | null // Transition duration in milliseconds
  wled_effect_settings?: WledEffectSettings | null // WLED effect configuration for this light in this scene
  created_at: string
  updated_at: string
}

export interface CreateSceneLightConfigData {
  scene_id: string
  light_id: string
  target_state: LightState
  transition_duration?: number | null
  wled_effect_settings?: WledEffectSettings | null
}

export interface UpdateSceneLightConfigData {
  target_state?: LightState
  transition_duration?: number | null
  wled_effect_settings?: WledEffectSettings | null
}

// Resolved config for light purposes with inheritance info
export interface ResolvedSceneLightConfig {
  scene_id: string
  light_id: string
  // Current values (either direct or inherited)
  target_state: LightState
  transition_duration: number | null
  wled_effect_settings?: WledEffectSettings | null
  // Inheritance tracking
  is_inherited: boolean
  inherited_from_scene_id: string | null
  inherited_from_scene_name: string | null
  // Direct config (null if inherited)
  direct_config: SceneLightConfig | null
}

export const useSceneLightConfigsStore = defineStore('sceneLightConfigs', () => {
  // State
  const configs = ref<SceneLightConfig[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Dependencies
  const authStore = useAuthStore()
  const scenesStore = useScenesStore()
  const lightsStore = useLightsStore()
  
  // Access store state - these are computed refs from the stores
  const scenes = computed(() => scenesStore.scenes)
  const setupLights = computed(() => lightsStore.setupLights)

  // Computed
  const configsBySceneId = computed(() => {
    return configs.value.reduce((acc, config) => {
      if (!acc[config.scene_id]) {
        acc[config.scene_id] = []
      }
      acc[config.scene_id].push(config)
      return acc
    }, {} as Record<string, SceneLightConfig[]>)
  })

  // Get configs for a specific scene
  const getConfigsForScene = computed(() => {
    return (sceneId: string) => configsBySceneId.value[sceneId] || []
  })

  // Get config for a specific scene-light combination
  const getConfig = computed(() => {
    return (sceneId: string, lightId: string) => {
      return configs.value.find(c => c.scene_id === sceneId && c.light_id === lightId) || null
    }
  })

  // Get resolved configs with inheritance for a scene  
  const getResolvedConfigsForScene = computed(() => {
    return (sceneId: string): ResolvedSceneLightConfig[] => {
      const scene = scenes.value.get(sceneId)
      if (!scene) return []

      // Get all lights that are in setup from the lights store
      const lightsInSetup = setupLights.value
      const resolvedConfigs: ResolvedSceneLightConfig[] = []

      // Build scene hierarchy by walking up parents (simplified version)
      const buildHierarchy = (currentSceneId: string): string[] => {
        const hierarchy = [currentSceneId]
        const currentScene = scenes.value.get(currentSceneId)
        if (currentScene?.parent_id) {
          hierarchy.push(...buildHierarchy(currentScene.parent_id))
        }
        return hierarchy
      }

      const sceneHierarchy = buildHierarchy(sceneId)

            for (const light of lightsInSetup) {
        let resolvedConfig: ResolvedSceneLightConfig = {
          scene_id: sceneId,
          light_id: light.id,
          target_state: { // Default values
            on: true,
            brightness: 254
          },
          transition_duration: null,
          wled_effect_settings: null,
          is_inherited: false,
          inherited_from_scene_id: null,
          inherited_from_scene_name: null,
          direct_config: null
        }

        // Look for direct config first
        const directConfig = getConfig.value(sceneId, light.id)
        if (directConfig) {
          resolvedConfig = {
            ...resolvedConfig,
            target_state: directConfig.target_state,
            transition_duration: directConfig.transition_duration,
            wled_effect_settings: directConfig.wled_effect_settings,
            direct_config: directConfig
          }
          
          // If direct config doesn't have wled_effect_settings, look for inherited ones
          if (!directConfig.wled_effect_settings) {
            for (let i = 1; i < sceneHierarchy.length; i++) {
              const parentSceneId = sceneHierarchy[i]
              const parentScene = scenes.value.get(parentSceneId)
              const parentConfig = getConfig.value(parentSceneId, light.id)
              if (parentConfig?.wled_effect_settings && parentScene) {
                resolvedConfig.wled_effect_settings = parentConfig.wled_effect_settings
                resolvedConfig.is_inherited = true
                resolvedConfig.inherited_from_scene_id = parentSceneId
                resolvedConfig.inherited_from_scene_name = parentScene.name
                break
              }
            }
          }
        } else {
          // No direct config - look for inherited config from parent scenes
          // First pass: find target_state from nearest parent
          let targetStateFound = false
          let wledEffectFound = false
          
          for (let i = 1; i < sceneHierarchy.length; i++) {
            const parentSceneId = sceneHierarchy[i]
            const parentScene = scenes.value.get(parentSceneId)
            const parentConfig = getConfig.value(parentSceneId, light.id)
            
            if (parentConfig && parentScene) {
              // Take target_state from the first parent that has a config
              if (!targetStateFound) {
                resolvedConfig.target_state = parentConfig.target_state
                resolvedConfig.transition_duration = parentConfig.transition_duration
                resolvedConfig.is_inherited = true
                resolvedConfig.inherited_from_scene_id = parentSceneId
                resolvedConfig.inherited_from_scene_name = parentScene.name
                targetStateFound = true
              }
              
              // Take wled_effect_settings from the first parent that has them
              if (!wledEffectFound && parentConfig.wled_effect_settings) {
                resolvedConfig.wled_effect_settings = parentConfig.wled_effect_settings
                // Update inherited_from if this is where we got the settings
                // (wled effects are more important for display)
                resolvedConfig.inherited_from_scene_id = parentSceneId
                resolvedConfig.inherited_from_scene_name = parentScene.name
                wledEffectFound = true
              }
              
              // Stop if we've found both
              if (targetStateFound && wledEffectFound) {
                break
              }
            }
          }
        }

        resolvedConfigs.push(resolvedConfig)
      }

      return resolvedConfigs
    }
  })

  // Get a single resolved config for a specific light (with inheritance)
  const getResolvedConfig = computed(() => {
    return (sceneId: string, lightId: string): ResolvedSceneLightConfig | null => {
      const resolvedConfigs = getResolvedConfigsForScene.value(sceneId)
      return resolvedConfigs.find(c => c.light_id === lightId) || null
    }
  })

  // Actions
  const fetchConfigs = async (): Promise<{ success: boolean; error?: string }> => {
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const data = await provider.query<SceneLightConfig>('scene_light_configs', {
        orderBy: [{ column: 'created_at', ascending: true }]
      })

      configs.value = data || []
      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch light configs'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      loading.value = false
    }
  }

  const createConfig = async (data: CreateSceneLightConfigData): Promise<{ success: boolean; data?: SceneLightConfig; error?: string }> => {
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const result = await provider.insert<SceneLightConfig>('scene_light_configs', {
        scene_id: data.scene_id,
        light_id: data.light_id,
        target_state: data.target_state,
        transition_duration: data.transition_duration,
        wled_effect_settings: data.wled_effect_settings
      })

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to create config')
      }

      configs.value.push(result.data)
      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create light config'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      loading.value = false
    }
  }

  const updateConfig = async (id: string, data: UpdateSceneLightConfigData): Promise<{ success: boolean; data?: SceneLightConfig; error?: string }> => {
    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const result = await provider.update<SceneLightConfig>('scene_light_configs', id, data)

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to update config')
      }

      // Replace the array to trigger reactivity
      const index = configs.value.findIndex(c => c.id === id)
      if (index !== -1) {
        configs.value = [
          ...configs.value.slice(0, index),
          result.data,
          ...configs.value.slice(index + 1)
        ]
      }

      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update light config'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      loading.value = false
    }
  }

  const deleteConfig = async (id: string): Promise<{ success: boolean; error?: string }> => {
    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const result = await provider.delete('scene_light_configs', id)

      if (!result.success) {
        throw new Error(result.error)
      }

      configs.value = configs.value.filter(c => c.id !== id)
      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete light config'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      loading.value = false
    }
  }

  // Convenience method to update or create config
  const setConfig = async (sceneId: string, lightId: string, data: UpdateSceneLightConfigData): Promise<{ success: boolean; error?: string }> => {
    const existingConfig = getConfig.value(sceneId, lightId)
    
    if (existingConfig) {
      return updateConfig(existingConfig.id, data)
    } else {
      // For creating new config, we need a complete target_state
      if (!data.target_state) {
        return { success: false, error: 'target_state is required for new config' }
      }
      
      const result = await createConfig({
        scene_id: sceneId,
        light_id: lightId,
        target_state: data.target_state,
        transition_duration: data.transition_duration
      })
      return { success: result.success, error: result.error }
    }
  }

  // Remove config to allow inheritance from parent
  const removeConfig = async (sceneId: string, lightId: string): Promise<{ success: boolean; error?: string }> => {
    const existingConfig = getConfig.value(sceneId, lightId)
    if (!existingConfig) {
      return { success: true } // Already removed
    }
    
    return deleteConfig(existingConfig.id)
  }

  // Update WLED effect settings for a light in a scene
  // Pass null to clear the effect settings
  const updateWledEffectSettings = async (sceneId: string, lightId: string, settings: WledEffectSettings | null): Promise<{ success: boolean; error?: string }> => {
    const existingConfig = getConfig.value(sceneId, lightId)
    
    console.log('[SceneLightConfigs] updateWledEffectSettings:', {
      sceneId,
      lightId,
      hasExistingConfig: !!existingConfig,
      gradientStops: settings?.gradient?.stops?.length,
      clearing: settings === null
    })
    
    if (existingConfig) {
      // Update existing config (null clears the settings)
      const result = await updateConfig(existingConfig.id, { wled_effect_settings: settings })
      console.log('[SceneLightConfigs] Updated existing config:', result)
      return result
    } else if (settings !== null) {
      // Create new config with default target_state and the effect settings
      // Don't create a config just to clear settings
      const result = await createConfig({
        scene_id: sceneId,
        light_id: lightId,
        target_state: { on: true, brightness: 254 },
        wled_effect_settings: settings
      })
      console.log('[SceneLightConfigs] Created new config:', result)
      return { success: result.success, error: result.error }
    } else {
      // No existing config and clearing - nothing to do
      return { success: true }
    }
  }

  return {
    // State
    configs,
    loading,
    error,

    // Computed
    configsBySceneId,
    getConfigsForScene,
    getConfig,
    getResolvedConfigsForScene,
    getResolvedConfig,

    // Actions
    fetchConfigs,
    createConfig,
    updateConfig,
    deleteConfig,
    setConfig,
    removeConfig,
    updateWledEffectSettings
  }
})